import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { IssueTypeBadge } from '@/components/IssueTypeBadge';
import { LoadingState } from '@/components/LoadingState';
import { StatusBadge } from '@/components/StatusBadge';
import { useIssue } from '../api';
import { AttachmentsList } from './AttachmentsList';
import { CommentsThread } from './CommentsThread';
import { MarkdownBody } from './MarkdownBody';

interface Props {
  id: string;
}

export function IssueDetail({ id }: Props) {
  const query = useIssue(id);

  if (query.isLoading) {
    return <LoadingState rows={5} />;
  }

  if (query.isError) {
    return (
      <div className="space-y-3" data-testid="bug-detail-error">
        <div className="text-sm text-destructive">Couldn&apos;t load issue.</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            Retry
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/issues">Back to issues</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!query.data) return null;
  const { issue, comments, attachments, stale } = query.data;

  return (
    <article className="space-y-6" data-testid="bug-detail-root" data-issue-id={issue.id}>
      {stale && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="bug-detail-stale-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            Live details for this issue are temporarily unavailable from Gitea. Showing the cached
            summary — description and comments can&apos;t be loaded right now. Try again shortly.
          </div>
        </div>
      )}
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <IssueTypeBadge type={issue.issueType} />
          <StatusBadge
            variant={issue.status === 'open' ? 'success' : 'neutral'}
            label={issue.status === 'open' ? 'Open' : 'Closed'}
            data-status={issue.status}
          />
        </div>
        <h1 className="text-2xl font-semibold" data-testid="bug-detail-title">
          {issue.title}
        </h1>
        <div className="text-sm text-muted-foreground">
          Submitted {formatDistanceToNow(new Date(issue.createdAt), { addSuffix: true })} ·{' '}
          {issue.giteaRepoName}#{issue.giteaIssueNumber}
        </div>
      </header>

      <Card data-testid="bug-detail-description">
        <CardContent className="p-4">
          {stale ? (
            <div className="text-sm text-muted-foreground">
              Description unavailable while Gitea is unreachable.
            </div>
          ) : (
            <MarkdownBody>{issue.body}</MarkdownBody>
          )}
        </CardContent>
      </Card>

      {/* Attachments panel renders even on the stale path — they're
          DB-only and don't depend on Gitea being reachable. */}
      <AttachmentsList attachments={attachments} />

      {!stale && <CommentsThread issueId={issue.id} comments={comments} />}
    </article>
  );
}
